// ./src/components/generic/ErrorState/ErrorState.tsx
import React from "react";
import { Stack, Text, Title, Button, ThemeIcon, Group } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";

import { ApiError } from "@/utils/apiResultHandler";

export interface ErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message/description - can be a string or ApiError */
  message?: string | ApiError | null;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Custom retry button text */
  retryLabel?: string;
  /** Whether retry is in progress */
  retrying?: boolean;
}

/**
 * Extracts the display message from a string or ApiError
 */
const getErrorMessage = (
  message: string | ApiError | null | undefined,
  fallback: string
): string => {
  if (!message) {
    return fallback;
  }

  if (typeof message === "string") {
    return message;
  }

  // It's an ApiError - use its message property
  return message.message || fallback;
};

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = "Something went wrong",
  message,
  onRetry,
  retryLabel = "Try again",
  retrying = false,
}) => {
  const displayMessage = getErrorMessage(
    message,
    "An error occurred. Please try again."
  );

  return (
    <Stack align="center" justify="center" gap="md" py="xl">
      <ThemeIcon size={64} radius="xl" color="red" variant="light">
        <IconAlertTriangle size={32} />
      </ThemeIcon>

      <Stack align="center" gap="xs">
        <Title order={4} ta="center">
          {title}
        </Title>
        <Text c="dimmed" ta="center" maw={400}>
          {displayMessage}
        </Text>
      </Stack>

      {onRetry && (
        <Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={onRetry}
            loading={retrying}
          >
            {retryLabel}
          </Button>
        </Group>
      )}
    </Stack>
  );
};